'use client';

import { OpenInNew } from '@mui/icons-material';
import { Box, Button, type ButtonProps, Typography } from '@mui/material';
import type { SystemStyleObject, Theme } from '@mui/system';
import { usePageAction } from '@repo/hooks/usePageAction';
import { requestIdleCallback } from '@repo/utils/requestIdleCallback';
import { type PropsWithChildren, startTransition, useState } from 'react';
import { CPCDialog } from './CPCDialog';
import type { CPCButtonAnalytics, CPCOfferProps } from './types';
import { WealthFrontDialog } from './WealthFrontDialog';

const linkStyles = {
  all: 'unset',
  display: 'inline-flex',
  alignItems: 'center',
  cursor: 'pointer',
  textDecoration: 'none',
  height: {
    xs: '44px',
    sm: '60px',
  },
  backgroundColor: 'transparent',
  boxShadow: 'none',
  minWidth: 0,
  '&:hover': {
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },
  '&:focus-visible': {
    boxShadow: 'none',
  },
};

const buttonBaseStyles = {
  gap: 1,
  whiteSpace: 'nowrap',
  height: 45,
  width: '100%',
  lineHeight: 1,
  padding: { xs: '8px 16px', md: '16px' },
  letterSpacing: '0.8125px',
  justifyContent: 'center',
  color: 'common.white',
  backgroundColor: 'primary.main',
  '&:hover': {
    backgroundColor: 'primary.dark',
  },
};

const defaultIconStyles = {
  display: 'inline-block',
  marginLeft: 0.5,
};

type CPCButtonProps = CPCOfferProps & {
  buttonAnalytics: CPCButtonAnalytics;
  /** Button style overrides. Must be a style object (arrays/functions not supported due to object spread merging). */
  buttonStyles?: SystemStyleObject<Theme>;
  /** Icon style overrides. Must be a style object (arrays/functions not supported due to object spread merging). */
  iconStyles?: SystemStyleObject<Theme>;
  /** Which partner modal opens on click. Defaults to the CPC marketing dialog. */
  modalVariant?: 'cpc' | 'wealthfront';
  variant?: 'default' | 'link';
  /** Wrapper style overrides. Must be a style object (arrays/functions not supported due to object spread merging). */
  wrapperStyles?: SystemStyleObject<Theme>;
};

export function CPCButton({
  buttonAnalytics,
  buttonStyles,
  children,
  iconStyles,
  modalVariant = 'cpc',
  cta,
  analyticsPayload,
  displayName,
  image,
  variant = 'default',
  wrapperStyles,
}: PropsWithChildren<CPCButtonProps>) {
  const [isOpen, setIsOpen] = useState(false);
  const trackPageAction = usePageAction({
    actionName: buttonAnalytics.actionName ?? 'cpc_modal_opened',
    actionSection: 'banking',
  });

  if (!cta) {
    return null;
  }

  const isLink = variant === 'link';
  const modalOfferProps = { cta, analyticsPayload, displayName, image };
  const buttonSx = {
    ...buttonBaseStyles,
    ...(isLink ? linkStyles : {}),
    ...buttonStyles,
  } as ButtonProps['sx'];
  const child = !isLink ? (
    <>
      <Typography
        variant="textSmall"
        sx={{
          textTransform: 'uppercase',
          letterSpacing: '0.8125px',
          fontWeight: 700,
        }}
      >
        {cta.text || 'Learn More'}
      </Typography>
      <OpenInNew
        sx={{
          ...defaultIconStyles,
          ...iconStyles,
        }}
      />
    </>
  ) : (
    children
  );
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '.25rem',
          ...wrapperStyles,
        }}
      >
        <Button
          disableRipple
          onClick={() => {
            requestAnimationFrame(() => {
              startTransition(() => setIsOpen(true));
            });
            requestIdleCallback(() => {
              trackPageAction();
            });
          }}
          sx={buttonSx}
        >
          {child}
        </Button>
        {cta.subtext && !isLink && (
          <Typography
            variant="textExtraSmall"
            sx={{
              color: 'neutral.darker',
              textAlign: 'center',
            }}
          >
            {cta.subtext}
          </Typography>
        )}
      </Box>
      {isOpen && modalVariant === 'wealthfront' && (
        <WealthFrontDialog
          buttonAnalytics={buttonAnalytics}
          {...modalOfferProps}
          open={isOpen}
          onClose={() => {
            requestAnimationFrame(() => {
              startTransition(() => setIsOpen(false));
            });
          }}
        />
      )}
      {isOpen && modalVariant === 'cpc' && (
        <CPCDialog
          buttonAnalytics={buttonAnalytics}
          {...modalOfferProps}
          open={isOpen}
          onClose={() => {
            requestAnimationFrame(() => {
              startTransition(() => setIsOpen(false));
            });
          }}
        />
      )}
    </>
  );
}
